import { Component, DestroyRef, OnInit, TemplateRef, inject, ChangeDetectionStrategy, viewChild } from '@angular/core';
import { Validators, FormBuilder, FormGroup, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { CitadelAutoCompleteComponent } from '@citadel/controls';
import { BehaviorSubject, combineLatest, debounceTime, mergeMap, ReplaySubject, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RegistrationService } from '../../services/registration-service.service';
import { SignInService } from '../../services/sign-in-service';
import { CitadelNotificationService, CitadelConfigurationService, CitadelErrorLoggingService } from '@citadel/core';
import { EmailValidationResult } from '../../models/email-validation-result.model';
import { RecaptchaService } from 'src/app/services/recaptcha.service';
import { ForgotPasswordModel } from 'src/app/models/forgot-password.model';

interface IAuthCredentials {
  returnUrl: string;
  userName: string;
  authenticationTypeL: number;
  requestID: string;
}

@Component({
  selector: 'app-sign-in-page',
  templateUrl: './sign-in-page.component.html',
  styleUrls: ['./sign-in-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SignInPageComponent implements OnInit {
  private http = inject(HttpClient);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private registration = inject(RegistrationService);
  private recaptchaService = inject(RecaptchaService);
  private notify = inject(CitadelNotificationService);
  private signInService = inject(SignInService);
  private config = inject(CitadelConfigurationService);
  private errorLogging = inject(CitadelErrorLoggingService);
  private destroyRef = inject(DestroyRef);

  readonly emailstep = viewChild<TemplateRef<any>>('emailstep');
  readonly otpchannel = viewChild<TemplateRef<any>>('otpchannel');
  readonly enterotp = viewChild<TemplateRef<any>>('enterotp');
  readonly password = viewChild<TemplateRef<any>>('password');
  readonly country = viewChild<CitadelAutoCompleteComponent>('country');

  protected selectedIndex = 0;
  protected steps: any = [
    { title: new BehaviorSubject<string>('Email') },
    { title: new BehaviorSubject<string>('Password') },
    { title: new BehaviorSubject<string>('One-time-PIN') },
  ];
  // Set only from the viewChild signals below, each of which is `TemplateRef<any> | undefined`
  // until its template exists in the DOM.
  protected currentTemplate?: TemplateRef<any>;
  // Both set synchronously in ngOnInit (setPasswordForm/setIdentityForm) before Angular's first
  // change-detection pass can read them.
  protected emailForm!: FormGroup;
  protected passwordForm!: FormGroup;
  private returnUrl: ReplaySubject<string> = new ReplaySubject<string>(1);
  // Two-way bound via [(ngModel)] in the template; unset until the user picks an OTP channel.
  protected otpType?: string;
  private otpValue: string = '';
  private score: number = 0;
  private recaptchaV3_token?: string;
  private otpSent?: boolean;
  private emailAddress: string = '';
  private defaultErrorMessage: string = 'An error occurred. Please try again';
  protected resendOtpText: string = 'Didn\'t receive the OTP?';

  protected otpTypeLabels = [
    { label: "SMS", value: "SMS" },
    { label: "WhatsApp", value: "WhatsApp" }
  ];

  protected value = {
    countryL: 0,
    dialingCode: 0,
    number: '',
  };

  private credentials: IAuthCredentials = {
    returnUrl: '',
    userName: '',
    authenticationTypeL: 0,
    requestID: ''
  };
  valAuth: boolean = true;

  protected get otpTypeLabel(): string {
    return this.otpType === 'SMS' ? 'phone messaging app' : this.otpType === 'WhatsApp' ? 'WhatsApp' : '';
  }

  // Dead code: never referenced from this component or its template.
  protected get scoreColor(): string {
    if (this.score > 0 && this.score <= 50) {
      return '#DF0303';
    }
    if (this.score > 50 && this.score <= 80) {
      return '#DF820D';
    }
    if (this.score > 80 && this.score <= 100) {
      return '#0DAC45';
    }
    return '';
  }

  // Dead field: declared, never assigned or read anywhere in this component.
  protected signInForm?: FormGroup;

  // convenience getter for easy access to form fields
  protected get usernameForm() {
 return this.emailForm.controls; 
}
  protected get pswForm() {
 return this.passwordForm.controls; 
}
  protected emailFormSubmitted: boolean = false;
  protected passwordFormSubmitted: boolean = false;
  protected showPinRequiredMsg: boolean = false;
  protected showChannelRequiredMsg: boolean = false;
  protected isSigningIn: boolean = false;

  private readonly signInContextKey = 'Citadel.Auth.Service.SignInContext';

  private impersonate = '';
  private routingState: { [k: string]: any; };
  constructor() {
    console.log('Auth server version: 2.0.0');
    this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: Loading sign-in context from sessionStorage`);
    this.routingState = {};
  }

  ngOnInit() {
    this.setPasswordForm();
    this.setIdentityForm();
    const returnUrlFromStorage = this.getStoredReturnUrl();

    this.setReturnUrl(returnUrlFromStorage);
    this.returnUrl.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(returnUrl => {
      if (returnUrl) {
        this.signInService.signInReturnUrl = returnUrl;
        const appID = this.loadFromUrl(returnUrl, 'client_id');
        this.impersonate = this.loadFromUrl(returnUrl, 'impersonate');
        this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: returnUrl found: ${returnUrl}, client_id: ${appID}, impersonate: ${this.impersonate}`);
        // this.maintenanceParams.setApplicationID(this.appID);
      } else {
        this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: No returnUrl found in sessionStorage, navigating to app`);
        this.navigateToApp();
      }
    });

    this.credentials = {
      returnUrl: returnUrlFromStorage || '',
      userName: '',
      authenticationTypeL: 0,
      requestID: ''
    };

    this.signInService.registrationReturnUrl = this.credentials.returnUrl;
  }

  private getStoredReturnUrl(): string {
    try {
      const raw = sessionStorage.getItem(this.signInContextKey);
      if (!raw) {
        return '';
      }

      const context = JSON.parse(raw);
      return context && typeof context.returnUrl === 'string' ? context.returnUrl : '';
    } catch {
      return '';
    }
  }

  private navigateToApp() {
    this.config.getGlobalSettingsLoaded().pipe(take(1)).subscribe(settings => {
      // BUG (found adopting strict mode, not fixed here): IAppSettings.rerouteAddress is declared
      // `string`, not a per-hostname map, so this index access can never resolve a real value at
      // runtime as written - see the identical site in app.component.ts.
      const rerouteAddress = (settings.rerouteAddress as unknown as Record<string, string> | undefined)?.[window.location.hostname];
      if (!!rerouteAddress) {
        window.location.href = rerouteAddress;
      } else {
        // This can only be used once the auth service runs under the main application
        //this.router.navigate(['..'], { relativeTo: this.route });
      }
    });
  }

  loadFromUrl(returnUrl: string, paramName: string): string {
    const regexResult = RegExp(`${paramName}=([\\.\\w-]+)`).exec(returnUrl);
    // const regexResult = RegExp(/client_id=([\.\w]+)&/).exec(this.returnUrl);
    if (regexResult && Array.isArray(regexResult) && regexResult.length >= 2 && typeof regexResult[1] === 'string') {
      return regexResult[1];
    }
    return '';
  }

  setPasswordForm(): void {
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required]]
    });
    this.attachUiLogging('password', this.passwordForm);
  }

  setIdentityForm(): void {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.attachUiLogging('email', this.emailForm);
  }

  private attachUiLogging(name: string, form: FormGroup) {
    form.statusChanges.subscribe(status => {
      if (status === 'INVALID') {
        this.logError(form, name);
      }
    });
  }
  private setReturnUrl(url: string) {
    this.returnUrl.next(url || '');
  }
  // private loadReturnURL() {
  //   this.config.getCurrentBrand().pipe(first()).subscribe(brand => {
  //     const firstReferrer = brand.Referrers.find((referrer: string) => !referrer.includes('auth'));
  //     this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: Loaded returnUrl from brand referrers: ${firstReferrer}`);
  //     this.returnUrl.next(firstReferrer || '/');
  //   });
  // }
  private logError(form: FormGroup<any>, name: string) {
    // BUG (found adopting strict mode, fixed here): form.errors[key] read form.errors again,
    // unguarded - if it was null (the `|| {}` only covered the Object.keys call), this threw.
    const formErrors = form.errors || {};
    const errors = Object.keys(formErrors).map(key => `${key}: ${formErrors[key]}`).join(', ');
    const values = Object.keys(form.value || {}).map(key => `${key}: ${key === 'password' ? form.value[key]?.length || 0 : form.value[key]}`).join(', ');
    this.errorLogging.logError('auth.input', `${name} Errors: ${errors}\nValue: ${values}`);
  }

  beforeValidateEmail() {
    this.recaptchaService.execute('validateEmail').subscribe((token) => {
      this.recaptchaV3_token = token;
      this.validateEmail();
    });
  }

  validateEmail() {
    if (!this.emailForm.value.email.match(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/)) {
      return;
    }
    this.returnUrl.pipe(mergeMap(returnUrl => this.http.post<ForgotPasswordModel>(`/api/auth/forgotpassword`,
      { email: this.emailForm.value.email, returnUrl: returnUrl, recaptchaToken: this.recaptchaV3_token },
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true, }))).subscribe({
        next: res => {
          if (res.passwordResetRequestId.match(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)
            && res.otpRequestId.match(/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/)) {

            this.signInService.passwordResetRequestId = res.passwordResetRequestId;
            this.signInService.passwordResetEmail = this.emailForm.value.email;
            this.signInService.otpResetRequestId = res.otpRequestId;
            //this.signInService.otpResetRequestId = this.credentials.requestID;
            this.router.navigateByUrl('/forgot-password', { state: { returnUrl: window.location.href } });
          }
        }, error: _error => {
          this.notify.error(this.defaultErrorMessage, "");
        }
      });
  }

  confirmEmail() {
    if (!this.emailForm.value.email.match(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/)) {
      return;
    }
    combineLatest([
      this.returnUrl,
      this.http.get<EmailValidationResult>(`/api/auth/${this.emailForm.value.email}`),
    ]).subscribe({
      next: ([returnUrl, res]) => {
        this.credentials.userName = res.userName;
        this.credentials.authenticationTypeL = res.authenticationTypeL;
        this.credentials.requestID = res.requestID;

        if (res.authenticationTypeL > 0) { // AD Account
          if (localStorage) {
            localStorage.setItem('Citadel.Auth.Service.AccountType', 'CitadelActiveDirectory');
          }
          window.location.href = `/external/challenge?userName=${this.credentials.userName}&impersonate=${this.impersonate}&returnUrl=${encodeURI(returnUrl)}`;
        } else {
          this.signInService.signInEmail = this.emailAddress;
          this.currentTemplate = this.password();
          this.selectedIndex = 1;
        }
      }, error: _error => {
        if (_error.error.message) {
          this.notify.error(_error.error.message, "");
        } else {
          this.notify.error(_error.message, "");
        }

        this.selectedIndex = 0;
      }
    });
  }

  updateIdentification(e: Event, id: string) {
    if (id === 'email') {
      (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/ /g, '');
    }
  }

  // Not read for its shape - only tested for truthiness.
  onOtpChannelChange(e: unknown): void {
    if (e && this.otpType) {
      this.showChannelRequiredMsg = false;
    }
  }

  updateOtp(e: string): void {
    if (e) {
      this.showPinRequiredMsg = false;
    }
    this.otpValue = e;
  }
  // Dead code: never called, and the body never returns a value (empty `if` block) - typed to what
  // it actually does, not to its old placeholder `any | null`.
  validateOtp(otp: string): void {
    if (!otp || otp.length < 4) {

    }

  }
  resendOTP(): void {
    this.otpSent = false;
    this.currentTemplate = this.otpchannel();
  }

  confirmPasswordValidator(controlName: string, matchingControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const control = formGroup.get(controlName);
      const matchingControl = formGroup.get(matchingControlName);

      if (control?.value !== matchingControl?.value) {
        matchingControl?.setErrors({ confirmPassword: true });
        return { confirmPassword: true };
      } else {
        matchingControl?.setErrors(null);
        return null;
      }
    };

  }

  // Dead code: never called anywhere in this component or its template.
  getStrength(): string {
    if (this.score > 0 && this.score <= 50) {
      // this.setBarColors(1, '#DF0303');
      return 'Weak';
    }
    if (this.score > 50 && this.score <= 80) {
      // this.setBarColors(2, '#DF820D');
      return 'Average';
    }
    if (this.score > 80 && this.score <= 100) {
      // this.setBarColors(3, '#0DAC45');
      return 'Strong';
    }
    return '';
  }

  goToSendOTP() {
    console.log('Submitting form');
    this.passwordFormSubmitted = true;
    // stop here if form is invalid
    if (this.passwordForm.invalid) {
      this.logError(this.passwordForm, 'password');
      this.notify.error('Please enter your password.', " ");
      return;
    }

    this.recaptchaService.execute('identifyClient').subscribe({
      next: (token) => {
        this.recaptchaV3_token = token;
        this.selectedIndex = 2;
        this.currentTemplate = this.otpchannel();
      },
      error: () => {
        this.notify.error('Error verifying the request (reCaptcha v3)', " ");
      }
    });
  }

  goToEnterOTP() {
    this.sendOtp();
  }

  goToPassword() {
    this.emailFormSubmitted = true;
    // stop here if form is invalid
    if (this.emailForm.invalid) {
      this.logError(this.emailForm, 'email');
      this.notify.error('Please enter your email address', '');
      return;
    }
    this.emailAddress = this.emailForm.controls.email.value;
    this.confirmEmail();
  }

  signIn() {
    if (this.isSigningIn) {
      return;
    }

    if (!this.otpValue || this.otpValue.length < 4) {
      if (this.otpValue.length === 0) {
        this.showPinRequiredMsg = true;
      }
      this.notify.error('A 4-digit OTP is required', " ");
    } else {
      this.showPinRequiredMsg = false;
      this.isSigningIn = true;
      this.credentials.returnUrl = this.signInService.signInReturnUrl || this.credentials.returnUrl;
      this.errorLogging.logError('auth-redirect-debug', `SignInPageComponent: Signing in with returnUrl: ${this.credentials.returnUrl}, userName: ${this.credentials.userName}, authenticationTypeL: ${this.credentials.authenticationTypeL}, requestID: ${this.credentials.requestID}`);

      try {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/api/auth/form';

        const fields: { [key: string]: string } = {
          returnUrl: this.credentials.returnUrl,
          userName: this.credentials.userName,
          authenticationTypeL: String(this.credentials.authenticationTypeL),
          requestID: this.credentials.requestID,
          password: this.passwordForm.value.password,
          otp: String(parseInt(this.otpValue)),
          correlationID: this.errorLogging.correlationID || crypto.randomUUID(),
        };

        for (const name of Object.keys(fields)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = fields[name];
          form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
      } catch (_error) {
        this.isSigningIn = false;
        this.notify.error(this.defaultErrorMessage, "");
      }
    }
  }

  private resetSignin() {
    this.emailForm.reset();
    this.passwordForm.reset();
    this.otpSent = false;
    this.currentTemplate = this.emailstep();
  }

  sendOtp(): void {
    if (!this.otpSent) {
      if (this.otpType) {
        this.registration.sendOtp(this.otpType, this.credentials.requestID)
          .pipe(debounceTime(2000))
          .subscribe({
            next: _ => {
              this.notify.success('OTP sent', "");
              this.selectedIndex = 2;
              this.currentTemplate = this.enterotp();
              this.otpSent = true;
            }, error: _ => this.notify.error(this.defaultErrorMessage, "")
          });
      } else {
        this.showChannelRequiredMsg = true;
        this.notify.error('Please select an option before you continue', " ");
      }
    }
  }

  handleKeyUp(e: KeyboardEvent, step: string) {
    if (e.keyCode === 13) {
      switch (step) {
        case "sendOtp":
          this.sendOtp();
          break;
        case "signIn":
          this.signIn();
          break;
      }
    }
  }
}
